export interface ContiguityPlanItem {
  id: string;
  run: number;
  shelf: number;
  x: number;
  w: number;
  hf: number;
  path: readonly (string | null)[];
}

interface PlanLike {
  all: () => ContiguityPlanItem[];
  nodeKey: (item: ContiguityPlanItem, depth: number) => string;
}

interface PlanHelpers {
  right: (item: ContiguityPlanItem) => number;
  width: (item: ContiguityPlanItem) => number;
}

export interface BlockFragment {
  node: string;
  run: number;
  shelves: readonly number[];
  ids: readonly string[];
  mainRun: number;
  mainShelves: readonly number[];
}

interface Band {
  run: number;
  shelves: number[];
  ids: string[];
  width: number;
}

const bandsOf = (members: readonly ContiguityPlanItem[], helpers: PlanHelpers): Band[] => {
  const byRun = new Map<number, ContiguityPlanItem[]>();
  for (const item of members) {
    const list = byRun.get(item.run) ?? [];
    list.push(item);
    byRun.set(item.run, list);
  }
  const bands: Band[] = [];
  for (const [run, items] of byRun) {
    const byShelf = new Map<number, ContiguityPlanItem[]>();
    for (const item of items) {
      const list = byShelf.get(item.shelf) ?? [];
      list.push(item);
      byShelf.set(item.shelf, list);
    }
    const shelves = [...byShelf.keys()].sort((a, b) => a - b);
    let current: Band | null = null;
    for (const shelf of shelves) {
      const onShelf = byShelf.get(shelf) ?? [];
      const width = onShelf.reduce((sum, item) => sum + helpers.width(item), 0);
      if (current && shelf === current.shelves[current.shelves.length - 1] + 1) {
        current.shelves.push(shelf);
        current.ids.push(...onShelf.map((item) => item.id));
        current.width += width;
      } else {
        current = { run, shelves: [shelf], ids: onShelf.map((item) => item.id), width };
        bands.push(current);
      }
    }
  }
  return bands;
};

// Subdivision, not scatter: one band per value, and fewer values than members (else any split "explains").
const bandsAreSubdivision = (
  bands: readonly Band[],
  members: readonly ContiguityPlanItem[],
  depth: number,
): boolean => {
  const bandOfId = new Map<string, number>();
  bands.forEach((band, index) => {
    for (const id of band.ids) bandOfId.set(id, index);
  });
  const levels = members[0]?.path.length ?? 0;
  for (let level = depth; level < levels; level += 1) {
    const bandsPerValue = new Map<string, Set<number>>();
    for (const item of members) {
      const value = String(item.path[level] ?? "");
      const seen = bandsPerValue.get(value) ?? new Set<number>();
      seen.add(bandOfId.get(item.id) ?? -1);
      bandsPerValue.set(value, seen);
    }
    if (bandsPerValue.size !== bands.length) continue;
    if (bandsPerValue.size >= members.length) continue;
    const whole = [...bandsPerValue.values()].every((seen) => seen.size === 1);
    if (!whole) continue;
    const covered = new Set([...bandsPerValue.values()].map((seen) => [...seen][0]));
    if (covered.size === bands.length) return true;
  }
  return false;
};

// Same-run band is a fragment only if foreign product owns a shelf between, inside the x-span.
export const verticalFragments = (
  plan: PlanLike,
  depth: number,
  helpers: PlanHelpers,
  onlyNode?: string,
): BlockFragment[] => {
  const all = plan.all();
  const byNode = new Map<string, ContiguityPlanItem[]>();
  for (const item of all) {
    const key = plan.nodeKey(item, depth);
    if (onlyNode !== undefined && key !== onlyNode) continue;
    const list = byNode.get(key) ?? [];
    list.push(item);
    byNode.set(key, list);
  }

  const fragments: BlockFragment[] = [];
  byNode.forEach((members, node) => {
    if (members.length < 2) return;
    const bands = bandsOf(members, helpers);
    if (bands.length < 2) return;
    if (bandsAreSubdivision(bands, members, depth)) return;
    const main = bands.reduce((best, band) =>
      band.width > best.width || (band.width === best.width && band.ids.length > best.ids.length)
        ? band
        : best,
    );
    for (const band of bands) {
      if (band === main) continue;
      if (band.run === main.run) {
        const bandLo = band.shelves[0];
        const bandHi = band.shelves[band.shelves.length - 1];
        const mainLo = main.shelves[0];
        const mainHi = main.shelves[main.shelves.length - 1];
        const betweenLo = Math.min(bandHi, mainHi) + 1;
        const betweenHi = Math.max(bandLo, mainLo) - 1;
        const onRun = members.filter((item) => item.run === band.run);
        const x0 = Math.min(...onRun.map((item) => item.x));
        const x1 = Math.max(...onRun.map((item) => helpers.right(item)));
        let separated = false;
        for (const item of all) {
          if (item.run !== band.run) continue;
          if (item.shelf < betweenLo || item.shelf > betweenHi) continue;
          if (plan.nodeKey(item, depth) === node) continue;
          if (item.x < x1 - 0.5 && helpers.right(item) > x0 + 0.5) {
            separated = true;
            break;
          }
        }
        if (!separated) continue;
      }
      fragments.push({
        node,
        run: band.run,
        shelves: [...band.shelves],
        ids: [...band.ids],
        mainRun: main.run,
        mainShelves: [...main.shelves],
      });
    }
  });
  return fragments;
};
